import { EditLimitationModel } from 'containers/Limitations/EditLimitationModel';
import { AdRequestSourceManager, DefaultAdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import {
  KeywordSource,
  MAX_TOTAL_KEYWORDS,
  Product,
  ProductAvailability,
  SuggestKeywords,
  createUnresolvedKeywordRow
} from 'core/product/Product';
import { RecommendKeywordsResponse } from 'ws/AdRequestSourceWebService';
import { useCallAPI } from 'hooks/useCallAPI';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './defaultSearchKeywordsComponent.module.scss';
import i18n from 'i18n';
import { compact, get, partial } from 'lodash';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { StickableBootstrapTable } from 'components/Table/StickableBootstrapTable';
import { TablePlaceholder } from 'components/TablePlaceholder/TablePlaceholder';
import { ColumnDefinition, renderColumn, sortableColumn } from 'components/TableColumn/TableColumn';
import formatters from './listFormatters';
import { ProductListOverview } from 'containers/RetailRMN/Products/ProductListOverview';
import { OPERATE } from 'enum/Operate';
import Button from 'components/Button/Button';
import { KeywordInput } from 'components/KeywordInput/KeywordInput';
import { ListEditBar } from 'components/ListEditBar/ListEditBar';
import { Modal } from 'components/Modal/Modal';
import { SearchBar } from 'components/SearchBar';
import { SelectOptions } from 'components/commonType';
import cx from 'classnames/bind';
import { FormControl, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faInfoCircle, faUndo } from '@fortawesome/free-solid-svg-icons';
import closeSmallIcon from 'assets/close-small.svg';
import Progress from 'components/Progress/Progress';
import { Status } from 'components/Status/Status';
import {
  COMPETITIVENESS_SORT_ORDER,
  COMPETITIVENESS_STATUS_COLOR,
  getKeywordBidCompetitiveness,
  getKeywordCoverage,
  toBidCap,
  KeywordBidCompetitiveness
} from 'core/pchome/KeywordBidCompetitiveness';
import { RtbOptimize } from 'core/rtbCampaign/RtbCampaign';

/**
 * 「出價試算」面板與外界的唯一接縫。
 *
 * 刻意合成一個 prop 而不是攤成六個：這些值全都只在 `RtbCampaignSetupStep` 的
 * `renderFormContent` 這一個 render scope 裡拿得到（`basicFormModel` 與
 * `formikProps` 都是那裡的區域變數），拆開只會把同一件事的簽章寬度乘六，
 * 而且省不掉任何一次 re-render —— 物件每次 render 都是新的，元件內部本來
 * 就得用 ref 存最新值、不把它放進 effect deps。
 */
export type BidSimulationReference = {
  /** `formikProps.values.optimize`：`getRecommendBidPriceRangeOf` 靠它挑 bidFloor。 */
  readonly optimize: RtbOptimize;
  /**
   * 經銷商出價下限（`getBidPriceRange(optimize).min`）。清單每一列的建議出價區間、
   * 出價試算面板的 capsule、覆蓋率都會被它抬高 ⇒ 必填,漏傳會讓這裡與 `基本資訊`
   * 各說一套。必須與上面的 `optimize` 由同一個值導出。
   */
  readonly bidFloor: number;
  /** 幣別（`basicFormModel.currency` = order 的幣別，缺值時是 NTD）。出價欄的前綴。 */
  readonly currency: string;
  /** `formikProps.values.bidPrice`：警示條要顯示的「此活動已有的出價」現值。 */
  readonly currentBidPrice?: number;
  /**
   * 建立模式下 `values.bidPrice` 是 `setupDefaultCampaign` 塞的預設值，不是使用者
   * 設過的出價 ⇒ 由呼叫端（唯一知道 `flowModel.type` 的地方）判定後傳進來，
   * 而不是在這裡用「值是不是 0」去猜。
   */
  readonly hasExistingBid: boolean;
  /**
   * 已選清單變動時餵回 `KeywordFormModel`，讓 `基本資訊` 的既有 hint 與
   * 「出價效益參考」卡片拿得到成效列。它刻意不 fireEvent，所以在 effect 裡呼叫
   * 是安全的、不會造成 render loop。
   */
  readonly rememberKeywordRows?: (rows: SuggestKeywords[]) => void;
  /**
   * remount 後用它把已選清單的成效 hydrate 回來（見 KeywordFormModel.recallKeywordRow）。
   * 少了它，這個字會退回全 0 的佔位列、區間顯示成預設的 8–20，而 `基本資訊` 的卡片讀的是
   * 累積式的 keywordRowMap、仍是真實區間 ⇒ 同一個字兩頁各說一套。
   */
  readonly recallKeywordRow?: (keyword: string) => SuggestKeywords | undefined;
  /** 用當前 draft（移除後的剩餘清單）算建議出價區間，跟著 draft 動態重算。 */
  readonly getRecommendBidPriceRangeOf?: (
    rows: SuggestKeywords[],
    optimize: RtbOptimize
  ) => { min: number, max: number };
  /** 「帶入下一步」＝ `formikProps.setFieldValue('bidPrice', bidCap)`。 */
  readonly setBidPrice: (bidPrice: number) => void;
};

export type SearchKeywordsComponentProps = {
  limitationModel: EditLimitationModel;
  products: Product[];
  bidPriceReference: BidSimulationReference;
  adRequestSourceManager?: AdRequestSourceManager;
};

const classNames = cx.bind(styles);
const defaultAdRequestSourceManager: AdRequestSourceManager = new DefaultAdRequestSourceManager();
const SUGGEST_KEYWORDS_THRESHOLD = 20;
const INITIAL_AUTOFILL_COUNT = 30;

export enum SelectedKeywordsListColumns {
  SUGGEST_KEYWORD = 'suggestKeyword',
  SOURCE = 'source',
  BID_PRICE = 'bidPrice',
  KEYWORD_SEARCH_VOLUME = 'keywordSearchVolume',
  LAST_7_DAY_GROWTH_RATE = 'last7DayGrowthRate',
  LAST_30_DAY_GROWTH_RATE = 'last30DayGrowthRate',
  KEYWORD_AD_COMPETITION = 'keywordAdCompetition',
  // 表頭 i18n key 是機械組出來的 `suggestKeywordsList.labels.${dataField}`
  // ⇒ 這個值必須正好是 `competitionStatus`，locale 檔已按此備好。
  COMPETITION_STATUS = 'competitionStatus',
  // 只有 (✕) 欄用，資料列上沒有這個欄位；formatter 走 (_cell, row)。
  REMOVE = 'remove'
}

type SidePanelMode = 'manual' | 'recommend' | 'bidSim' | null;

export const SearchKeywordsComponent: React.FC<SearchKeywordsComponentProps> = ({
  limitationModel,
  products,
  bidPriceReference,
  adRequestSourceManager = defaultAdRequestSourceManager
}) => {

  // === Initial read from persisted limitation state ===
  // Read the raw SelectOptions (not just keyword strings) so we can pick up
  // BE-supplied `extra.sources` per keyword. LimitationUtil.wrapLimitations-
  // FromServer promotes BE's top-level `sources` back into `extra.sources`
  // for keyword types; if the source list is present we hydrate it, otherwise
  // fall back to the split-by-type default ([MANUAL] for searchKeywords,
  // [SYSTEM] for recommendedKeywords).
  const { recallKeywordRow } = bidPriceReference;
  const persistedKeywords: SuggestKeywords[] = useMemo(() => {
    const readOptions = (type: string): SelectOptions[] => {
      const bucket: any[] = get(limitationModel.limitationValue, OPERATE.INCLUDE, []);
      const entry = bucket.find((ta: any) => ta.type === type);
      const value = entry?.value;
      return Array.isArray(value) ? value as SelectOptions[] : [];
    };
    const toRow = (opt: SelectOptions, fallback: KeywordSource): SuggestKeywords => {
      const keyword = opt.value.toString();
      const optSources = opt.extra?.sources;
      const sources = Array.isArray(optSources) && optSources.length > 0 ? optSources : [fallback];
      // 先問模型記不記得這個字的成效。limitation 只存 value / label / sources，
      // 純從它重建出來的是全 0 佔位列 ⇒ 每次切 tab 都會把真實區間打回預設的 8–20，
      // 而 `基本資訊` 的卡片讀的是累積式的 keywordRowMap、不受 remount 影響
      // ⇒ 同一個字兩頁顯示不同區間。hydrate 是讓兩邊由構造一致，不是各修一次。
      // sources 一律以 limitation 為準:map 裡 enrichedSelected 那批沒有 sources。
      const known = recallKeywordRow ? recallKeywordRow(keyword) : undefined;
      return known ? { ...known, sources } : createUnresolvedKeywordRow(keyword, sources);
    };
    const manualOpts = readOptions('searchKeywords');
    const recOpts = readOptions('recommendedKeywords');
    const manualKeywordSet = new Set(manualOpts.map(o => o.value.toString()));
    const manualRows = manualOpts.map(opt => toRow(opt, KeywordSource.MANUAL));
    const recRows = recOpts
      .filter(opt => !manualKeywordSet.has(opt.value.toString()))
      .map(opt => toRow(opt, KeywordSource.SYSTEM));
    // 手動輸入的字排在前面。這裡只負責 mount 那一刻讀回來的順序；畫面上真正
    // 把手動字釘在最上面的是 selectedTableData，兩邊給出的順序要一致。
    return [...manualRows, ...recRows];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [errors, setErrors] = useState<any>(limitationModel.state.errors);
  const [inited, setInited] = useState<boolean>(
    persistedKeywords.length > 0 && get(errors, `${OPERATE.INCLUDE}.recommendedKeywords`) === undefined
  );
  const [selectedList, setSelectedList] = useState<SuggestKeywords[]>(persistedKeywords);
  const [selectedListChecked, setSelectedListChecked] = useState<Set<string>>(new Set());
  const [recommendPool, setRecommendPool] = useState<SuggestKeywords[]>([]);
  const [sidePanelMode, setSidePanelMode] = useState<SidePanelMode>(null);
  const [manualPending, setManualPending] = useState<string[]>([]);
  const [recommendChecked, setRecommendChecked] = useState<Set<string>>(new Set());
  const [recommendSearch, setRecommendSearch] = useState<string>('');
  const [selectedSearch, setSelectedSearch] = useState<string>('');
  // 「出價試算」面板的 draft 只記「被移除的關鍵字」（保留移除順序給 undo chip 用），
  // 剩餘清單一律由 selectedList 現算 —— 這樣 /recommendKeywords 的 enrichedSelected
  // 回寫（P5）進來時，面板上的建議出價／熱搜度會跟著更新，而不是卡在開面板那一刻的快照。
  const [bidSimRemoved, setBidSimRemoved] = useState<string[]>([]);
  const [bidSimBidCapInput, setBidSimBidCapInput] = useState<string>('');
  const recommendSearchBarRef = useRef<SearchBar>(null);
  const { callAPIs, loading } = useCallAPI();

  // prop 物件每次 render 都是新的 ⇒ 存進 ref，讓下面的 effect deps 只放 selectedList，
  // 否則每次 render 都會重跑 effect → setState → 再 render，無限迴圈。
  const bidPriceReferenceRef = useRef<BidSimulationReference>(bidPriceReference);
  useEffect(() => {
    bidPriceReferenceRef.current = bidPriceReference;
  });

  const selectedKeywordSet = useMemo(
    () => new Set(selectedList.map(row => row.suggestKeyword)),
    [selectedList]
  );

  const filteredSelectedList = useMemo(() => {
    const term = selectedSearch.trim().toLowerCase();
    return term
      ? selectedList.filter(r => r.suggestKeyword.toLowerCase().includes(term))
      : selectedList;
  }, [selectedList, selectedSearch]);

  // === Fetch ===
  // Single unified call to /recommendKeywords (D3 + D15). Loads recommend pool
  // AND enriches persisted / manually-typed keywords with metrics. `autoFill`
  // gates one-shot seeding of top-30 SYSTEM_RECOMMEND rows for create-mode
  // (empty persisted state); handlers pass false so re-fetches never re-seed.
  const productIds = useMemo(
    () => products.filter(p => p.availability === ProductAvailability.IN_STOCK).map(p => p.productId),
    [products]
  );

  const fetchRecommendKeywords = useCallback((currentList: SuggestKeywords[], autoFill: boolean) => {
    callAPIs([
      () => adRequestSourceManager.getRecommendKeywords({
        goodsCodes: productIds,
        selectedKeywords: currentList.map(r => r.suggestKeyword)
      })
    ], (response: RecommendKeywordsResponse) => {
      // 只有 /recommendKeywords 回來的列可以餵回 KeywordFormModel。
      // 不能改用「selectedList 一變就餵」——本元件在切 tab 時會被上層的
      // key={model.activeTab} 整個 remount，而 remount 後的 selectedList 是從
      // limitation 重建的全 0 佔位列（limitation 只存關鍵字與來源，不存成效），
      // 那樣會把已經 enrich 過的成效列蓋回佔位列：真實區間會悄悄變成預設的 8–20，
      // 而那個值看起來完全合理（不像 0–0 那樣明顯是壞的）⇒ 更難被發現。
      // keywordRowMap 是累積式的，把佔位列餵進去只會覆蓋掉已知的好資料，救不回來。
      // （卡片端「缺列要補佔位列」是讀取時現算的，見 KeywordFormModel.resolveKeywordRows；
      //   兩者不衝突：一個管「不要寫髒」，一個管「不要漏顯示」。）
      const reference = bidPriceReferenceRef.current;
      if (reference.rememberKeywordRows) {
        reference.rememberKeywordRows([...response.recommendPool, ...response.enrichedSelected]);
      }
      setRecommendPool(response.recommendPool);
      if (response.enrichedSelected.length > 0) {
        const enrichMap = new Map(response.enrichedSelected.map(row => [row.suggestKeyword, row]));
        setSelectedList(prev => prev.map(row => {
          const enriched = enrichMap.get(row.suggestKeyword);
          return enriched ? { ...enriched, sources: row.sources } : row;
        }));
      }
      if (autoFill) {
        const seeded = response.recommendPool
          .filter(row => row.sources.includes(KeywordSource.SYSTEM))
          .slice(0, INITIAL_AUTOFILL_COUNT)
          .map(row => ({ ...row, sources: [KeywordSource.SYSTEM] }));
        if (seeded.length > 0) {
          setSelectedList(prev => {
            const seen = new Set(prev.map(r => r.suggestKeyword));
            return [...prev, ...seeded.filter(r => !seen.has(r.suggestKeyword))];
          });
        }
      }
    }, (error: any) => {
      console.error('fetchRecommendKeywords error', error);
      setRecommendPool([]);
    });
  }, [adRequestSourceManager, callAPIs, productIds]);

  const persistToLimitationModel = useCallback((list: SuggestKeywords[]) => {
    const toOption = (row: SuggestKeywords): SelectOptions => ({
      label: row.suggestKeyword,
      value: row.suggestKeyword,
      extra: { sources: row.sources }
    });
    const manual = list.filter(r => r.sources.includes(KeywordSource.MANUAL)).map(toOption);
    const recommended = list.filter(r => !r.sources.includes(KeywordSource.MANUAL)).map(toOption);
    limitationModel.setLimitation(OPERATE.INCLUDE, 'searchKeywords', manual);
    limitationModel.setLimitation(OPERATE.INCLUDE, 'recommendedKeywords', recommended);
  }, [limitationModel]);

  useEffect(() => {
    persistToLimitationModel(selectedList);
  }, [selectedList, persistToLimitationModel]);

  useEffect(() => {
    if (!inited && selectedList.length > 0) {
      setInited(true);
    }
  }, [selectedList, inited]);

  // === Mount fetch ===
  // One /recommendKeywords call on mount. Seeds pool + enriches persisted rows
  // + auto-fills top-30 SYSTEM rows on empty create-mode state. Runs exactly
  // once; subsequent fetches are handler-driven (post manual-add).
  useEffect(() => {
    const autoFill = !inited && selectedList.length === 0;
    fetchRecommendKeywords(selectedList, autoFill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = limitationModel.event.add(model => {
      setErrors(model.state.errors);
    });
    return () => { limitationModel.event.remove(handler); };
  }, [limitationModel]);

  // === Handlers ===
  const handleSelOnSelect = useCallback((id: string | number, select: boolean) => {
    setSelectedListChecked(prev => {
      const next = new Set(prev);
      if (select) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  }, []);

  const handleSelOnSelectPage = useCallback((select: boolean) => {
    setSelectedListChecked(prev => {
      const next = new Set(prev);
      if (select) filteredSelectedList.forEach(r => next.add(r.suggestKeyword));
      else filteredSelectedList.forEach(r => next.delete(r.suggestKeyword));
      return next;
    });
  }, [filteredSelectedList]);

  const handleSelOnRemoveAllSelect = useCallback(() => {
    setSelectedListChecked(new Set());
  }, []);

  const handleDeleteChecked = useCallback(() => {
    setSelectedList(prev => prev.filter(r => !selectedListChecked.has(r.suggestKeyword)));
    setSelectedListChecked(new Set());
  }, [selectedListChecked]);

  const handleOpenManual = useCallback(() => {
    setManualPending([]);
    setSidePanelMode('manual');
  }, []);

  const handleOpenRecommend = useCallback(() => {
    setRecommendChecked(new Set());
    setRecommendSearch('');
    setSidePanelMode('recommend');
  }, []);

  // draft 的清空只放在 handleClosePanel 一處（開啟時不再清一次）：面板一定是先關
  // 才能再開，兩邊都清是冗餘的，而冗餘的守衛沒有任何一個測試殺得掉。
  const handleOpenBidSimulation = useCallback(() => {
    setSidePanelMode('bidSim');
  }, []);

  const handleClosePanel = useCallback(() => {
    setSidePanelMode(null);
    setManualPending([]);
    setRecommendChecked(new Set());
    setRecommendSearch('');
    // 關閉／取消 = 放棄 draft：已移除的關鍵字不保留，下次開面板是乾淨的。
    setBidSimRemoved([]);
    setBidSimBidCapInput('');
  }, []);

  const handleConfirmManual = useCallback(() => {
    const seen = new Set(selectedList.map(r => r.suggestKeyword));
    const additions = manualPending
      .filter(k => !seen.has(k))
      .map(k => createUnresolvedKeywordRow(k, [KeywordSource.MANUAL]));
    // 放在最前面而不是接在後面：使用者剛打完字，看不到它就會以為沒有輸入成功，
    // 那正是這個畫面被回報的問題。
    const nextList = [...additions, ...selectedList];
    setSelectedList(nextList);
    handleClosePanel();
    if (additions.length > 0) {
      fetchRecommendKeywords(nextList, false);
    }
  }, [selectedList, manualPending, handleClosePanel, fetchRecommendKeywords]);

  const handleConfirmRecommend = useCallback(() => {
    setSelectedList(prev => {
      const seen = new Set(prev.map(r => r.suggestKeyword));
      const additions = recommendPool
        .filter(row => recommendChecked.has(row.suggestKeyword))
        .filter(row => !seen.has(row.suggestKeyword));
      return [...prev, ...additions];
    });
    handleClosePanel();
  }, [recommendPool, recommendChecked, handleClosePanel]);

  const handleAddSearchAsManual = useCallback(() => {
    const keyword = recommendSearch.trim();
    if (!keyword) return;
    const already = selectedList.some(r => r.suggestKeyword === keyword);
    if (!already) {
      const nextList = [createUnresolvedKeywordRow(keyword, [KeywordSource.MANUAL]), ...selectedList];
      setSelectedList(nextList);
      fetchRecommendKeywords(nextList, false);
    }
    handleClosePanel();
  }, [recommendSearch, selectedList, fetchRecommendKeywords, handleClosePanel]);

  // === 「出價試算」面板 ===
  // draft 一律由 selectedList 現算，state 只記「被移除了哪些字」（陣列而非 Set，
  // 因為 undo chip 要按移除順序排）。
  const bidSimRemovedSet = useMemo(() => new Set(bidSimRemoved), [bidSimRemoved]);

  const bidSimDraft = useMemo(
    () => selectedList.filter(row => !bidSimRemovedSet.has(row.suggestKeyword)),
    [selectedList, bidSimRemovedSet]
  );

  // 空字串 / 非數字 / 非正數一律當「還沒輸入」⇒ 磚顯示破折號、進度 bar 不出現、
  // 主 CTA disabled。`undefined` 也正好是 getKeywordBidCompetitiveness 的 NOT_EVALUATED 入口。
  const bidSimBidCap = useMemo(() => toBidCap(bidSimBidCapInput), [bidSimBidCapInput]);

  // 經銷商出價下限。取成區域變數是為了進 useMemo deps —— 直接寫 bidPriceReference
  // 會讓每次 render 都重算（那個物件刻意每次都是新的,見 BidSimulationReference 的註解）。
  const bidFloor = bidPriceReference.bidFloor;

  // `bidFloor` 不在 row 裡,而它隨「優化目標」變、row 不變 ⇒ 建議出價區間那一欄
  // 光靠「bidPrice 變動才重畫」接不住它。Cell 認 `row` 與 `formatExtraData` 的變動,
  // 所以三個區間欄都要帶這份 extraData;少了它,切 CPC↔CPM 後區間會停在舊值而且不報錯。
  const bidRangeExtraData = useMemo(() => ({ bidFloor }), [bidFloor]);

  // 判定一律走已凍結的共用模組，不在這裡寫比較式（`基本資訊` 的出價效益參考卡片
  // 用的是同一組函式，兩頁一致是由構造保證的）。
  const bidSimCoverage = useMemo(
    () => getKeywordCoverage(bidSimBidCap, bidSimDraft, bidFloor),
    [bidSimBidCap, bidSimDraft, bidFloor]
  );

  // 區間跟著 draft 動態重算（移除一個字，區間就跟著變）。clamp 與 8/20 fallback
  // 都留在 model 那一份，這裡不重寫。
  const bidSimRecommendRange = useMemo(() => {
    const getRange = bidPriceReference.getRecommendBidPriceRangeOf;
    return getRange ? getRange(bidSimDraft, bidPriceReference.optimize) : undefined;
  }, [bidPriceReference, bidSimDraft]);

  const handleBidSimBidCapChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setBidSimBidCapInput(event.target.value);
  }, []);

  const handleBidSimRemove = useCallback((keyword: string) => {
    setBidSimRemoved(prev => prev.includes(keyword) ? prev : [...prev, keyword]);
  }, []);

  const handleBidSimRestore = useCallback((keyword: string) => {
    setBidSimRemoved(prev => prev.filter(removed => removed !== keyword));
  }, []);

  const handleConfirmBidSimulation = useCallback(() => {
    if (bidSimBidCap === undefined) {
      return;
    }
    // 上層 RtbCampaignSetupStep 的 key={model.activeTab} 會在切 tab 時把本元件整個
    // remount、draft 一併銷毀 ⇒ 寫回已選清單與寫回 Formik 必須在同一個 handler 內
    // 同步做完。本元件也刻意不自己切 tab：只要多一個「先切 tab 再提交」的路徑，
    // 這一段就會斷。
    //
    // 用 functional update 只套用「移除」這個 delta，而不是 setSelectedList(draft)。
    // /recommendKeywords 的 enrichedSelected 回寫也是 functional update，兩者這樣才會
    // 串成序列；直接塞面板快照會把剛回填的成效數字打回零。
    setSelectedList(prev => prev.filter(row => !bidSimRemovedSet.has(row.suggestKeyword)));
    // Formik 的 enableReinitialize 只在 initialValues 的內容真的變了才 resetForm
    // （formik 內部是 isEqual 深比較，不是物件識別）。flowModel.state.campaign 只有
    // setCampaign 會換，fetchCampaignAnalytics 不碰它 ⇒ 這裡寫進去的值不會被洗掉。
    bidPriceReferenceRef.current.setBidPrice(bidSimBidCap);
    // 使用者接著切到 `基本資訊` 時，若剩餘關鍵字 < 20 會彈既有的 alert modal
    // （KeywordFormModel.processAlertStep 的 keywordsSuggestThreshold = 20）。
    // 那是既有行為，不是這個面板造成的，刻意不繞過。
    handleClosePanel();
  }, [bidSimBidCap, bidSimRemovedSet, handleClosePanel]);

  // 競爭狀態算好後寫進資料列，而不是留在 formatter 裡從閉包讀 bidCap：
  // StickableBootstrapTable 底下的 react-bootstrap-table-next 只認 `data` 的變動，
  // 光換 columns / formatter 的函式識別不會讓已渲染的 cell 重畫（實測：出價一改，
  // 磚上的數字更新了，capsule 卻整排卡在「未試算」）。
  const bidSimTableData = useMemo(
    () => bidSimDraft.map(row => ({
      ...row,
      [SelectedKeywordsListColumns.COMPETITION_STATUS]:
        getKeywordBidCompetitiveness(bidSimBidCap, row, bidFloor)
    })),
    [bidSimDraft, bidSimBidCap, bidFloor]
  );

  const bidSimTableColumns = useMemo(() => {
    // 可排序的欄位與「關鍵字推薦」那張表一致：成效欄可排，關鍵字本身不排。
    const columnDefinition = (
      columnName: string,
      customLabel?: string,
      sortable: boolean = false
    ): ColumnDefinition => ({
      ...sortableColumn(
        columnName,
        customLabel !== undefined ? customLabel : `suggestKeywordsList.labels.${columnName}`,
        sortable
      ),
      classes: () => styles[columnName],
      headerClasses: () => styles[columnName]
    });
    // 不允許移除到 0 個：後端的 Preconditions.checkArgument(!keywords.isEmpty()) 會 500，
    // 而且 limitation validation 會把使用者強制拉回 LIMITATION tab。
    const keepLast = bidSimDraft.length <= 1;
    return compact([
      renderColumn(
        columnDefinition(SelectedKeywordsListColumns.REMOVE, ''),
        (_cell: any, row: SuggestKeywords) => (
          <button
            type='button'
            className={styles.removeCell}
            disabled={keepLast}
            title={keepLast
              ? i18n.t<string>('limitation.labels.bidSimulationKeepLastKeyword')
              : i18n.t<string>('limitation.labels.bidSimulationRemoveKeyword', { keyword: row.suggestKeyword })}
            aria-label={i18n.t<string>('limitation.labels.bidSimulationRemoveKeyword', { keyword: row.suggestKeyword })}
            onClick={partial(handleBidSimRemove, row.suggestKeyword)}
          >
            <img src={closeSmallIcon} alt='' />
          </button>
        ),
        // 空表頭：這一欄沒有標題文字。
        () => <></>
      ),
      renderColumn(
        columnDefinition(
          SelectedKeywordsListColumns.SUGGEST_KEYWORD,
          i18n.t<string>('suggestKeywordsList.labels.suggestKeyword')
        ),
        formatters.nameFormatter,
        formatters.nameHeaderFormatter
      ),
      renderColumn(
        {
          ...columnDefinition(SelectedKeywordsListColumns.BID_PRICE, 'suggestKeywordsList.labels.bidPriceRange', true),
          formatExtraData: bidRangeExtraData
        },
        (bidPrice: number, row: SuggestKeywords) =>
          formatters.bidRangeFormatter(bidPrice, row, bidFloor)
      ),
      renderColumn(
        columnDefinition(SelectedKeywordsListColumns.KEYWORD_SEARCH_VOLUME, undefined, true),
        formatters.keywordSearchVolumeFormatter
      ),
      renderColumn(
        columnDefinition(SelectedKeywordsListColumns.KEYWORD_AD_COMPETITION, undefined, true),
        formatters.keywordAdCompetitionFormatter
      ),
      renderColumn(
        {
          ...columnDefinition(SelectedKeywordsListColumns.COMPETITION_STATUS, undefined, true),
          // cell 是 KeywordBidCompetitiveness 的字串值，直接排會變成按字母 ⇒ 換成名次。
          sortValue: (cell: KeywordBidCompetitiveness) => COMPETITIVENESS_SORT_ORDER[cell]
        },
        // <Status> 內建 CopyableOverlayTrigger 的 hover overlay；不給
        // renderExtraInfo 就不會有 overlay，這裡刻意留空。
        (competitiveness: KeywordBidCompetitiveness) => (
          <Status
            color={COMPETITIVENESS_STATUS_COLOR[competitiveness]}
            label={i18n.t<string>(`suggestKeywordsList.labels.competitionStatuses.${competitiveness}`)}
          />
        )
      )
    ]);
  }, [bidSimDraft.length, handleBidSimRemove, bidFloor, bidRangeExtraData]);

  // Extracted from the JSX to satisfy tslint's no-lambda-in-jsx. Reuses the
  // TablePlaceholder + wrapper shape StickableBootstrapTable renders for
  // its default string-based noDataIndication; when a search is active
  // with no matches, appends the "add as manual" CTA below.
  const renderRecommendNoData = useCallback(() => {
    const q = recommendSearch.trim();
    const placeholderKey = q
      ? 'limitation.labels.recommendNoMatchHint'
      : 'limitation.labels.recommendEmpty';
    return (
      <div className={styles.recNoDataPlaceholder}>
        <TablePlaceholder placeholder={i18n.t<string>(placeholderKey, { keyword: q })} />
        {q && (
          <Button primary onClick={handleAddSearchAsManual}>
            {i18n.t<string>('limitation.buttons.addKeywordAsManual', { keyword: q })}
          </Button>
        )}
      </div>
    );
  }, [recommendSearch, handleAddSearchAsManual]);

  const selectedTableError = get(errors, `${OPERATE.INCLUDE}.searchKeywords`)
    || get(errors, `${OPERATE.INCLUDE}.recommendedKeywords`);

  // === Table columns for the selected-keywords table ===
  const selectedTableColumns = useMemo(() => {
    const columnDefinition = (columnName: string, sortable: boolean = false, customLabel?: string): ColumnDefinition => ({
      ...sortableColumn(columnName, customLabel ? customLabel : `suggestKeywordsList.labels.${columnName}`, sortable),
      classes: () => styles[columnName],
      headerClasses: () => styles[columnName]
    });
    const keywordColumn = renderColumn(
      columnDefinition(SelectedKeywordsListColumns.SUGGEST_KEYWORD, false, i18n.t<string>('suggestKeywordsList.labels.suggestKeyword')),
      formatters.nameFormatter,
      formatters.nameHeaderFormatter
    );
    const sourceColumn = renderColumn(
      columnDefinition(SelectedKeywordsListColumns.SOURCE, false, 'suggestKeywordsList.labels.source'),
      (_v: any, row: SuggestKeywords) => formatters.sourceFormatter(row.sources)
    );
    const bidRangeColumn = renderColumn(
      {
        ...columnDefinition(SelectedKeywordsListColumns.BID_PRICE, false, 'suggestKeywordsList.labels.bidPriceRange'),
        formatExtraData: bidRangeExtraData
      },
      (bidPrice: number, row: SuggestKeywords) =>
        formatters.bidRangeFormatter(bidPrice, row, bidFloor)
    );
    const searchVolumeColumn = renderColumn(
      columnDefinition(SelectedKeywordsListColumns.KEYWORD_SEARCH_VOLUME),
      formatters.keywordSearchVolumeFormatter
    );
    const growth7Column = renderColumn(
      columnDefinition(SelectedKeywordsListColumns.LAST_7_DAY_GROWTH_RATE),
      formatters.growthRateFormatter
    );
    const growth30Column = renderColumn(
      columnDefinition(SelectedKeywordsListColumns.LAST_30_DAY_GROWTH_RATE),
      formatters.growthRateFormatter
    );
    const competitionColumn = renderColumn(
      columnDefinition(SelectedKeywordsListColumns.KEYWORD_AD_COMPETITION),
      formatters.keywordAdCompetitionFormatter
    );
    return compact([
      keywordColumn,
      sourceColumn,
      bidRangeColumn,
      searchVolumeColumn,
      growth7Column,
      growth30Column,
      competitionColumn
    ]);
  }, [bidFloor, bidRangeExtraData]);

  const filteredRecommendPool = useMemo(() => {
    const q = recommendSearch.trim().toLowerCase();
    return recommendPool
      .filter(row => !selectedKeywordSet.has(row.suggestKeyword))
      .filter(row => !q || row.suggestKeyword.toLowerCase().includes(q));
  }, [recommendPool, selectedKeywordSet, recommendSearch]);

  const handleRecOnSelect = useCallback((id: string | number, select: boolean) => {
    setRecommendChecked(prev => {
      const next = new Set(prev);
      if (select) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  }, []);

  const handleRecOnSelectPage = useCallback((select: boolean) => {
    setRecommendChecked(prev => {
      const next = new Set(prev);
      if (select) filteredRecommendPool.forEach(r => next.add(r.suggestKeyword));
      else filteredRecommendPool.forEach(r => next.delete(r.suggestKeyword));
      return next;
    });
  }, [filteredRecommendPool]);

  const handleRecOnRemoveAllSelect = useCallback(() => {
    setRecommendChecked(new Set());
  }, []);

  const selectedRecommendRows = useMemo(
    () => Array.from(recommendChecked),
    [recommendChecked]
  );

  const selectedListRows = useMemo(
    () => Array.from(selectedListChecked),
    [selectedListChecked]
  );

  // 手動輸入的字一律排在最上面，不分它是這次加的還是上次存檔留下來的。
  // 用分桶而不是 sort：分桶天生穩定，兩組各自維持原有的相對順序。
  // 放在這裡是因為它是所有來源的唯一匯流處，也最靠近 render；表格自己的
  // defaultSorted 用不了，remote 會讓本地排序整個失效。
  const selectedTableData = useMemo(() => {
    const rows = filteredSelectedList.map(row => ({ ...row, id: row.suggestKeyword }));
    return [
      ...rows.filter(row => row.sources.includes(KeywordSource.MANUAL)),
      ...rows.filter(row => !row.sources.includes(KeywordSource.MANUAL))
    ];
  }, [filteredSelectedList]);

  const recommendTableData = useMemo(
    () => filteredRecommendPool.map(row => ({ ...row, id: row.suggestKeyword })),
    [filteredRecommendPool]
  );

  const recommendTableColumns = useMemo(() => {
    const columnDefinition = (columnName: string, sortable: boolean = false, customLabel?: string): ColumnDefinition => ({
      ...sortableColumn(columnName, customLabel ? customLabel : `suggestKeywordsList.labels.${columnName}`, sortable),
      classes: () => styles[columnName],
      headerClasses: () => styles[columnName]
    });
    return compact([
      renderColumn(
        columnDefinition(SelectedKeywordsListColumns.SUGGEST_KEYWORD, false, i18n.t<string>('limitation.labels.selectAllKeywords')),
        (suggestKeyword: string) => (
          <div className={styles.selectedNameCell}>{suggestKeyword}</div>
        )
      ),
      renderColumn(
        columnDefinition(SelectedKeywordsListColumns.SOURCE, false, 'suggestKeywordsList.labels.source'),
        (_v: any, row: SuggestKeywords) => formatters.sourceFormatter(row.sources)
      ),
      renderColumn(
        {
          ...columnDefinition(SelectedKeywordsListColumns.BID_PRICE, true, 'suggestKeywordsList.labels.bidPriceRange'),
          formatExtraData: bidRangeExtraData
        },
        (bidPrice: number, row: SuggestKeywords) =>
          formatters.bidRangeFormatter(bidPrice, row, bidFloor)
      ),
      renderColumn(
        columnDefinition(SelectedKeywordsListColumns.KEYWORD_SEARCH_VOLUME, true),
        formatters.keywordSearchVolumeFormatter
      ),
      renderColumn(
        columnDefinition(SelectedKeywordsListColumns.LAST_7_DAY_GROWTH_RATE, true),
        formatters.growthRateFormatter
      ),
      renderColumn(
        columnDefinition(SelectedKeywordsListColumns.LAST_30_DAY_GROWTH_RATE, true),
        formatters.growthRateFormatter
      ),
      renderColumn(
        columnDefinition(SelectedKeywordsListColumns.KEYWORD_AD_COMPETITION, true),
        formatters.keywordAdCompetitionFormatter
      )
    ]);
  }, [bidFloor, bidRangeExtraData]);

  // 四個分支、三個色階：紅色只留給「超過上限」。剛好 100 組是合法的設定值而不是
  // 錯誤狀態，所以它跟一般狀態同色，只是文字改成「已達上限」而不是「還可再新增 0 組」。
  const renderHeaderHint = useCallback(() => {
    const total = selectedList.length;
    const renderChip = (tone: string, i18nKey: string, values: Record<string, number>) => (
      <span className={classNames('keywordCountChip', tone)}>
        {i18n.t<string>(i18nKey, values)}
      </span>
    );
    if (total < SUGGEST_KEYWORDS_THRESHOLD) {
      return renderChip('isWarning', 'limitation.labels.searchKeywordsTitleWarning', {
        count: total,
        suggest: SUGGEST_KEYWORDS_THRESHOLD,
        remaining: SUGGEST_KEYWORDS_THRESHOLD - total
      });
    }
    // 超過上限的活動只可能來自匯入訂單或複製活動，那兩條路徑不經過這個畫面，
    // 後端也沒有擋。此時使用者連存檔都會被 validator 擋住，所以要讓他知道
    // 多了幾組、要刪掉幾個字才救得回來。
    if (total > MAX_TOTAL_KEYWORDS) {
      return renderChip('isError', 'limitation.labels.searchKeywordsTitleOverHint', {
        count: total,
        exceed: total - MAX_TOTAL_KEYWORDS
      });
    }
    if (total === MAX_TOTAL_KEYWORDS) {
      return renderChip('isNormal', 'limitation.labels.searchKeywordsTitleAtLimit', {
        max: MAX_TOTAL_KEYWORDS
      });
    }
    return renderChip('isNormal', 'limitation.labels.searchKeywordsTitleTotalHint', {
      count: total,
      remaining: MAX_TOTAL_KEYWORDS - total
    });
  }, [selectedList.length]);

  // 藍色那條是一次性的說明，紅色那條說的是「為什麼存不下去」。兩者在任何視窗
  // 高度都會顯示 —— 矮螢幕是改用縮短列表來讓出空間，而不是把說明條藏起來：
  // 一般筆電的可視高度本來就低於那個門檻，藏起來等於藍色那條永遠不會出現。
  const renderKeywordsBanner = () => {
    // 剛好 100 組還不算「加不進去」——那是允許的上限值，畫面也沒有擋，所以這裡用
    // 嚴格大於：只有已經超過的設定（匯入訂單或複製活動帶進來的）才需要先刪掉幾個字。
    // 代價是 100 組時藍色那條仍鼓勵繼續新增，而加到 101 組存檔會被 limitation
    // 驗證擋下；這是產品刻意選的方向，寧可讓剛好用滿的人不被誤導。
    const overLimit = selectedList.length > MAX_TOTAL_KEYWORDS;
    return (
      <div className={classNames('keywordsBanner', overLimit ? 'isAtLimit' : 'isInfo')}>
        <FontAwesomeIcon icon={overLimit ? faExclamationTriangle : faInfoCircle} />
        <span>
          {i18n.t<string>(overLimit
            ? 'limitation.labels.searchKeywordsBannerAtLimit'
            : 'limitation.labels.searchKeywordsBannerInfo')}
        </span>
      </div>
    );
  };

  // className 只給「出價試算」用（bidSimSubtitle 疊淺藍底）——「手動輸入」那個面板
  // 維持原本的白底。
  const renderPanelSubtitle = (i18nKeys: string[], className?: string) => (
    <div className={classNames('panelSubtitle', className)}>
      {i18nKeys.map(key => (
        <div key={key}>{i18n.t<string>(key)}</div>
      ))}
    </div>
  );

  const renderManualBody = () => (
    <div className={styles.manualBody}>
      {renderPanelSubtitle(['limitation.labels.manualInputSubtitle'])}
      <div className={styles.descript}>
        {i18n.t<string>('limitation.labels.searchKeywordsInputDes')}
        {/* 數的是這次面板裡打進去的字（manualPending），不是列表上已經加好的手動字。
            使用者剛打了三個字卻看到「已經輸入了 40 組」，那個 40 講的是另一件事。
            建議組數的提示已經移除，所以這裡只剩單純的計數，沒有警示色階。 */}
        <span className={styles.searchKeywordsTitlePostText}>
          {i18n.t<string>('limitation.labels.searchKeywordsTitleHint', { count: manualPending.length })}
        </span>
      </div>
      <KeywordInput
        value={manualPending}
        onChange={setManualPending}
      />
    </div>
  );

  const renderRecommendBody = () => (
    <div className={styles.recommendBody}>
      {recommendChecked.size > 0 ? (
        <ListEditBar
          selectedItems={Array.from(recommendChecked)}
          handleRemoveSelect={handleRecOnRemoveAllSelect}
        />
      ) : (
        <div className={styles.recSearch}>
          <SearchBar
            ref={recommendSearchBarRef}
            model={{
              placeholder: i18n.t<string>('keywordsProposalList.placeholders.searchbar'),
              defaultValue: recommendSearch,
              search: setRecommendSearch
            }}
          />
        </div>
      )}
      <div className={styles.recTableWrapper}>
        <StickableBootstrapTable
          keyField={SelectedKeywordsListColumns.SUGGEST_KEYWORD}
          selectFieldName='suggestKeyword'
          data={recommendTableData}
          columns={recommendTableColumns}
          selectedRows={selectedRecommendRows}
          onSelect={handleRecOnSelect}
          onSelectPage={handleRecOnSelectPage}
          onRemoveAllSelect={handleRecOnRemoveAllSelect}
          noDataIndication={renderRecommendNoData}
          hidePagination
        />
      </div>
    </div>
  );

  const renderBidSimulationBody = () => {
    const emptyValue = i18n.t<string>('limitation.labels.bidSimulationEmptyValue');
    const evaluated = bidSimBidCap !== undefined;
    // 建立模式下 values.bidPrice 是預設值不是使用者設過的出價 ⇒ 不顯示警示條。
    const showExistingBidWarning = bidPriceReference.hasExistingBid && !!bidPriceReference.currentBidPrice;
    return (
      <div className={styles.bidSimBody}>
        {renderPanelSubtitle([
          'limitation.labels.bidSimulationSubtitle',
          'limitation.labels.bidSimulationSubtitleHint'
        ], 'bidSimSubtitle')}
        {showExistingBidWarning && (
          <div className={styles.bidSimWarningBar}>
            {/* `assets/warning.svg` 其實是藍色的 info 圓圈（fill 寫死 #0090EB），
                放在琥珀色警示條裡既不是警示語意、顏色也對不上。改用 repo 既有的警示 icon
                （`CreativeReviewItem` 也是這一支），它吃 `currentColor` ⇒ 自動跟著本條的
                文字色，不會再有寫死的顏色。 */}
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>
              {i18n.t<string>('limitation.labels.bidSimulationExistingBidWarning', {
                bidCap: bidPriceReference.currentBidPrice
              })}
            </span>
          </div>
        )}
        <div className={styles.bidSimBidCapRow}>
          <label htmlFor='bidSimulationBidCap'>
            {i18n.t<string>('limitation.labels.bidSimulationBidCap')}
          </label>
          {/* 幣別前綴，與 `基本資訊` 的出價上限欄一致（那邊是
              `<FormikField.InputGroup prefix={currency}>`）。這裡不能直接用那個元件：
              面板的出價是 draft、只在按「確認」時才寫進 formik，綁成 formik
              field 會變成每打一個字就改 `bidPrice`。所以組同一組 react-bootstrap 節點 ——
              `InputGroup.Text` 的樣式來自 inputGroupField.module.scss 的 `:global`，
              兩邊長得完全一樣。
              只有前綴、沒有「元」後綴。 */}
          <InputGroup className={styles.bidSimBidCapInput}>
            <InputGroup.Prepend>
              <InputGroup.Text>{bidPriceReference.currency}</InputGroup.Text>
            </InputGroup.Prepend>
            <FormControl
              id='bidSimulationBidCap'
              name='bidSimulationBidCap'
              type='number'
              min={0}
              value={bidSimBidCapInput}
              onChange={handleBidSimBidCapChange}
              autoComplete='off'
            />
          </InputGroup>
          {bidSimRecommendRange && (
            <span className={styles.bidSimRecommendRange}>
              {i18n.t<string>('limitation.labels.bidSimulationRecommendRange', {
                min: bidSimRecommendRange.min,
                max: bidSimRecommendRange.max
              })}
            </span>
          )}
        </div>
        {/* 兩個數字磚：白底橫幅、上下各一條淺藍線、中間一條分隔線，兩半各自置中。
            這裡刻意不用 InfoDisplaySection —— 它把 `grid-template-columns`
            （`repeat(auto-fill, <px>)`）寫在 inline style 上，要改成「兩等分」只能用
            `!important` 打贏 inline；而它預設是「小灰 label 在上、value 在下、靠左」，
            欄數／順序／對齊／字級／顏色全部覆寫之後，能重用的只剩三個 div。 */}
        <div className={styles.bidSimTiles}>
          <div className={styles.bidSimTile}>
            {/* 預設藍；只有在有暫時移除的字時才轉琥珀（提示「這個數字是在你調整過的
                清單上算的」）。空狀態沒有移除任何字 ⇒ 仍是藍色。 */}
            <div
              className={classNames('bidSimTileValue', {
                bidSimTileValueRemoved: bidSimRemoved.length > 0
              })}
            >
              {`${evaluated ? bidSimCoverage.competitiveCount : emptyValue} / ${bidSimCoverage.total}`}
            </div>
            <div className={styles.bidSimTileLabel}>
              {i18n.t<string>('limitation.labels.bidSimulationCompetitiveCount')}
            </div>
          </div>
          <div className={styles.bidSimTile}>
            <div className={styles.bidSimTileValue}>
              {evaluated ? `${bidSimCoverage.coverageRate}%` : emptyValue}
            </div>
            <div className={styles.bidSimTileLabel}>
              {i18n.t<string>('limitation.labels.bidSimulationCoverageRate')}
            </div>
          </div>
        </div>
        {/* <Progress> 吃 0~1、內部自己 ×100。未輸入出價時整條不顯示。 */}
        {evaluated && (
          <div className={styles.bidSimProgress}>
            <Progress theme1 progress={bidSimCoverage.coverageRate / 100} />
          </div>
        )}
        {bidSimRemoved.length > 0 && (
          <div className={styles.bidSimRemovedBar}>
            <span className={styles.bidSimRemovedLabel}>
              {i18n.t<string>('limitation.labels.bidSimulationRemoved')}
            </span>
            {bidSimRemoved.map(keyword => (
              <button
                key={keyword}
                type='button'
                className={styles.bidSimUndoChip}
                title={i18n.t<string>('limitation.labels.bidSimulationRestoreKeyword', { keyword })}
                aria-label={i18n.t<string>('limitation.labels.bidSimulationRestoreKeyword', { keyword })}
                onClick={partial(handleBidSimRestore, keyword)}
              >
                <FontAwesomeIcon icon={faUndo} />
                {keyword}
              </button>
            ))}
          </div>
        )}
        {/* 這張表刻意不傳 onSelect/selectedRows —— 那會讓第一欄被
            selectableColumnFormatter 整個覆寫成 checkbox，(✕) 欄就沒了。 */}
        <div className={styles.bidSimTableWrapper}>
          <StickableBootstrapTable
            keyField={SelectedKeywordsListColumns.SUGGEST_KEYWORD}
            data={bidSimTableData}
            columns={bidSimTableColumns}
            noDataIndication={'limitation.labels.selectedKeywordsEmpty'}
            hidePagination
          />
        </div>
        {/* footer 主 CTA 下方的灰字 hint 塞不進 Modal.Footer（它固定 74px 且
            flex-direction: row-reverse）⇒ 放 children 的最底部。 */}
        <div className={styles.bidSimConfirmHint}>
          {i18n.t<string>('limitation.labels.bidSimulationConfirmHint', {
            bidCap: evaluated ? bidSimBidCap : emptyValue
          })}
        </div>
      </div>
    );
  };

  const renderBidSimulationPanel = () => {
    const evaluated = bidSimBidCap !== undefined;
    return (
      <Modal
        sliderScreen
        // 標題列的淺藍底與藍字只作用在這個面板 —— Modal.scss 是全域的，不能直接改。
        className='keywordBidSimulationPanel'
        title={i18n.t<string>('limitation.labels.bidSimulationPanelTitle')}
        primaryButton={{
          title: evaluated
            ? i18n.t<string>('limitation.buttons.confirmBidSimulation', { bidCap: bidSimBidCap })
            : i18n.t<string>('limitation.buttons.bidSimulationNeedBidCap'),
          callback: handleConfirmBidSimulation,
          disabled: !evaluated
        }}
        secondaryButton={{
          title: i18n.t<string>('common.buttons.cancel'),
          callback: handleClosePanel
        }}
        dismiss={handleClosePanel}
        enableCloseBtn
        enforceFocus
        animation
      >
        {renderBidSimulationBody()}
      </Modal>
    );
  };

  const renderSidePanel = () => {
    if (sidePanelMode === null) return null;
    if (sidePanelMode === 'bidSim') return renderBidSimulationPanel();
    const isManual = sidePanelMode === 'manual';
    const title = isManual
      ? i18n.t<string>('limitation.labels.manualInputPanelTitle')
      : i18n.t<string>('limitation.labels.recommendPanelTitle');
    const canConfirm = isManual
      ? manualPending.length > 0
      : recommendChecked.size > 0;
    return (
      <Modal
        sliderScreen
        title={title}
        primaryButton={{
          title: i18n.t<string>(isManual ? 'limitation.buttons.confirmAdd' : 'limitation.buttons.addToSelected'),
          callback: isManual ? handleConfirmManual : handleConfirmRecommend,
          disabled: !canConfirm
        }}
        secondaryButton={{
          title: i18n.t<string>('common.buttons.cancel'),
          callback: handleClosePanel
        }}
        dismiss={handleClosePanel}
        enableCloseBtn
        enforceFocus
        animation
      >
        {isManual ? renderManualBody() : renderRecommendBody()}
      </Modal>
    );
  };

  return (
    <fieldset className={styles.searchKeywords}>
      {(!inited && loading) && <LoadingIndicator />}
      <legend>
        <span>{i18n.t<string>('limitation.labels.selectedProducts')}</span>
      </legend>
      <div className={styles.selectedProductsArea}>
        <ProductListOverview products={products} defaultOpen={false} />
      </div>
      {selectedListChecked.size > 0 ? (
        // 包一層是為了把操作列的高度壓成跟標題列一樣。ListEditBar 是共用元件，
        // 活動 / 素材 / 廣告群組三個列表頁也在用，不能改它自己的高度。
        <div className={styles.editBarSlot}>
          <ListEditBar
            selectedItems={selectedListRows}
            handleRemoveSelect={handleSelOnRemoveAllSelect}
          >
            <ListEditBar.Item>
              <ListEditBar.Link onClick={handleDeleteChecked}>
                {i18n.t<string>('limitation.buttons.deleteSelectedKeywords')}
              </ListEditBar.Link>
            </ListEditBar.Item>
          </ListEditBar>
        </div>
      ) : (
        <div className={styles.legendBar}>
          <legend>
            <span>{i18n.t<string>('limitation.labels.searchKeywordsTitle')}</span>
            {renderHeaderHint()}
          </legend>
          <div className={styles.selectedSearchBar}>
            <SearchBar
              model={{
                placeholder: i18n.t<string>('limitation.placeholders.selectedKeywordsSearchbar'),
                defaultValue: selectedSearch,
                search: setSelectedSearch
              }}
            />
          </div>
        </div>
      )}
      {/* 掛在標題列之外：勾選任一列時整條標題列會被 ListEditBar 換掉，而勾選正是
          使用者要刪字的時候，「請先刪掉不需要的關鍵字」這句話此時最需要留著。 */}
      {renderKeywordsBanner()}
      <div className={styles.keywordSettingContainer}>
        <div className={styles.selectedArea}>
          <div className={styles.selectedTableWrapper}>
            {loading && <LoadingIndicator />}
            <StickableBootstrapTable
              stickFirstColumn
              scrollByElementControl={false}
              keyField={SelectedKeywordsListColumns.SUGGEST_KEYWORD}
              selectFieldName='suggestKeyword'
              data={selectedTableData}
              columns={selectedTableColumns}
              selectedRows={selectedListRows}
              onSelect={handleSelOnSelect}
              onSelectPage={handleSelOnSelectPage}
              onRemoveAllSelect={handleSelOnRemoveAllSelect}
              noDataIndication={'limitation.labels.selectedKeywordsEmpty'}
              remote={true}
              hidePagination
            />
          </div>
          {selectedTableError && (
            <div className={styles.tableErrorMsg}>
              {Array.isArray(selectedTableError)
                ? selectedTableError.join('; ')
                : selectedTableError}
            </div>
          )}
          <div className={styles.actionButtons}>
            <Button secondary onClick={handleOpenManual}>
              {i18n.t<string>('limitation.buttons.openManualInput')}
            </Button>
            <Button secondary onClick={handleOpenRecommend}>
              {i18n.t<string>('limitation.buttons.openRecommendPanel')}
            </Button>
            <Button primary onClick={handleOpenBidSimulation}>
              {i18n.t<string>('limitation.buttons.openBidSimulation')}
            </Button>
          </div>
        </div>
        {renderSidePanel()}
      </div>
    </fieldset>
  );
};
